// FROM https://medium.com/@fast.nguyen/build-otp-input-with-reactjs-hooks-5699eb58b427
import * as _ from 'lodash';
import { CSSProperties, useCallback, useState } from 'react';
import * as React from 'react';
import SingleOTPInput from './SingleOTPInput';
import { styled } from '../../theme';
import { DeviceMaxWidth } from '../../utils/device';

export interface OTPInputProps {
  name?: string;
  length: number; // Number of inputs
  otpValues: string[];
  onChange: (otp: string[]) => any; // Handle onOTPChange to use its value

  autoFocus?: boolean; // Auto focus to input programmatically
  isNumberInput?: boolean; // If otp is number
  disabled?: boolean;
  error?: boolean;

  style?: CSSProperties; // Style for container OTP
  className?: any; // Class for container OTP

  inputStyle?: CSSProperties; // Style for input
  inputClassName?: string; // Class for input
}

export function OTPInput(props: OTPInputProps) {
  const {
    otpValues: specifiedOTPValues,
    length,
    isNumberInput,
    autoFocus,
    disabled,
    error,
    onChange,
    inputClassName,
    inputStyle,
    ...rest
  } = props;

  // Pad OTP codes to make input element controlled
  const otpValues = _.assign(new Array(length).fill(''), specifiedOTPValues);

  // Define state activeInput = 0
  const [activeInput, setActiveInput] = useState(0);

  // helpers
  // Focus `inputIndex` input
  const focusInput = useCallback(
    (inputIndex: number) => {
      // prevent focus on an unreachable input index
      const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);
      setActiveInput(selectedIndex);
    },
    [length]
  );

  // Helper to return OTP from inputs
  const handleOtpChange = useCallback(
    (otp: string[]) => {
      onChange(otp);
    },
    [onChange]
  );

  // Change OTP value at focussing input
  const changeCodeAtFocus = useCallback(
    (str: string) => {
      const updatedOTPValues = [...otpValues];
      updatedOTPValues[activeInput] = str[0] || '';
      handleOtpChange(updatedOTPValues);
    },
    [activeInput, handleOtpChange, otpValues]
  );

  const focusNextInput = useCallback(() => {
    focusInput(activeInput + 1);
  }, [activeInput, focusInput]);

  const focusPrevInput = useCallback(() => {
    focusInput(activeInput - 1);
  }, [activeInput, focusInput]);

  // Helper to return value with the right type: 'text' or 'number'
  const getRightValue = useCallback(
    (str: string) => {
      let changedValue = str;
      if (!isNumberInput) {
        return changedValue;
      }
      return !changedValue || /\d/.test(changedValue) ? changedValue : '';
    },
    [isNumberInput]
  );

  // Handle onFocus input
  const handleOnFocus = useCallback(
    (index: number) => () => {
      focusInput(index);
    },
    [focusInput]
  );

  // Handle onChange value for each input
  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = getRightValue(e.currentTarget.value);
      if (!val) {
        e.preventDefault();
        return;
      }
      changeCodeAtFocus(val);
      focusNextInput();
    },
    [changeCodeAtFocus, focusNextInput, getRightValue]
  );
  // Handle onBlur input
  const handleOnBlur = useCallback(() => {
    setActiveInput(-1);
  }, []);

  // Handle onKeyDown input
  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'Backspace':
        case 'Delete': {
          e.preventDefault();
          if (otpValues[activeInput]) {
            changeCodeAtFocus('');
          } else {
            focusPrevInput();
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          focusPrevInput();
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          focusNextInput();
          break;
        }
        case ' ': {
          e.preventDefault();
          break;
        }
        default:
          break;
      }
    },
    [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues]
  );
  // Handle onPaste input
  const handleOnPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData('text/plain')
        .trim()
        .slice(0, length - activeInput)
        .split('');
      if (pastedData) {
        let nextFocusIndex = 0;
        const updatedOTPValues = [...otpValues];
        updatedOTPValues.forEach((val, index) => {
          if (index >= activeInput) {
            const changedValue = getRightValue(pastedData.shift() || val);
            if (changedValue) {
              updatedOTPValues[index] = changedValue;
              nextFocusIndex = index;
            }
          }
        });
        setActiveInput(Math.min(nextFocusIndex + 1, length - 1));
        handleOtpChange(updatedOTPValues);
      }
    },
    [activeInput, getRightValue, length, otpValues]
  );

  return (
    <S.Container {...rest}>
      {Array(length)
        .fill('')
        .map((_, index) => (
          <S.InputWrapper key={`SingleInput-wrap-${index}`}>
            <SingleOTPInput
              key={`SingleInput-${index}`}
              focus={activeInput === index}
              autoFocus={autoFocus}
              value={otpValues && otpValues[index]}
              onFocus={handleOnFocus(index)}
              onChange={handleOnChange}
              onKeyDown={handleOnKeyDown}
              onBlur={handleOnBlur}
              onPaste={handleOnPaste}
              style={inputStyle}
              className={inputClassName}
              disabled={disabled}
              error={error}
              type={props.isNumberInput ? 'number' : 'text'}
            />
          </S.InputWrapper>
        ))}
    </S.Container>
  );
}

const BASE_LABEL = 'otp-input-widget';
namespace S {
  export const Container = styled.div`
    label: ${BASE_LABEL};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    max-width: 300px;
    @media (${DeviceMaxWidth.mobileS}) {
      max-width: 280px;
    }
  `;
  export const InputWrapper = styled.div`
    label: ${BASE_LABEL}_input;
    flex-grow: 1;
    height: 40px;
    max-width: 60px;
    margin: 5px;
  `;
}
